import CONSTANTS from 'utils/constants';
import _ from 'ckLodash';
import elementUtils from 'utils/elementUtils';
import findElementUtils from 'utils/findElementUtils';
import guardUtils from 'utils/guardUtils';
import rangeUtils from 'utils/rangeUtils';
import selectionUtils from 'utils/selectionUtils';
import utils from 'utils/utils';

var NAVIGATION_KEYS = [
    CONSTANTS.PAGE_UP_KEYCODE,
    CONSTANTS.PAGE_DOWN_KEYCODE,
    CONSTANTS.END_KEYCODE,
    CONSTANTS.HOME_KEYCODE,
    CONSTANTS.LEFT_KEYCODE,
    CONSTANTS.UP_KEYCODE,
    CONSTANTS.RIGHT_KEYCODE,
    CONSTANTS.DOWN_KEYCODE
];
var MODIFIER_KEYS = [
    CONSTANTS.SHIFT_KEYCODE,
    CONSTANTS.CONTROL_KEYCODE,
    CONSTANTS.ALT_KEYCODE
];
var STOP_KEY_EVENT_ON_EMPTY_EDITOR = [
    CONSTANTS.BACKSPACE_KEYCODE,
    CONSTANTS.DELETE_KEYCODE
].concat(NAVIGATION_KEYS);

function countBrTagsTillEndSelection(editor, startElement) {
    startElement = startElement || editor.editable();
    var range = new CKEDITOR.dom.range(editor.editable());
    range.selectNodeContents(startElement);
    var selectedRange = selectionUtils.getSelectedRange(editor);
    range.setEnd(selectedRange.endContainer, selectedRange.endOffset);
    return rangeUtils.countBrInRange(range);
}

function isEmptyEditor(editor) {
    if (utils.getEditorTextLength(editor) > 0) {
        //there is still text character, the editor is not empty
        return false;
    }

    if (editor.editable().getChildCount() > 1) {
        //text is empty but we have a few text blocks (a few lines)
        //backspace should not be prevent, text is not empty
        return false;
    }

    var brCount = countBrTagsTillEndSelection(editor);
    return brCount === 0; //if there are no br's, text is empty - should prevent backspace
}

function stopEvent(event) {
    event.data.$.preventDefault();
    event.data.$.stopPropagation();
    event.stop();
}

function isBackspace(keyCode) {
    return keyCode === CONSTANTS.BACKSPACE_KEYCODE;
}

function isDeleteKey(keyCode) {
    return keyCode === CONSTANTS.DELETE_KEYCODE;
}

function shouldStopKeyDownEvent(editor, keyCode) {
    return _.includes(STOP_KEY_EVENT_ON_EMPTY_EDITOR, keyCode) && isEmptyEditor(editor);
}

function isShiftEnter(event) {
    var keyCode = event.data.$.keyCode;
    if (!event.data.$.shiftKey) {
        return false;
    }
    return keyCode === CONSTANTS.RETURN_KEYCODE || keyCode === CONSTANTS.ENTER_KEYCODE;
}


function shouldIgnoreKeyDownEvent(event) {
    var keyCode = event.data.$.keyCode;
    return _.includes(NAVIGATION_KEYS, keyCode) ||
        _.includes(MODIFIER_KEYS, keyCode) ||
        event.data.$.metaKey;
}

function isCombiningKeys(event) {
    var keyCode = event.data.$.keyCode;
    return keyCode === 229;
}

function selectionStartsOnFirstBlock(editor) {
    return selectionUtils.getSelectedRange(editor).startPath().contains(findElementUtils.findFirstTextBlock(editor));
}

function setCursorOutOfGuardIfNeeded(editor) {
    //SE-15541: ck's removeStyle is making us problems - inside styleCommand.fixCurrentStyle -
    //by unnecessarily splitting nodes causing us
    //to have 3 guards in one block, 2 of them in an invalid state ( no guard char inside ).
    //this happens when ckeditor sets its cursor inside our guard, adding its filling char inside our guard
    //and .... well, things go wrong
    //lets avoid that by carefully placing the cursor after our guard when we "unlist" a previously existing list
    var guard = _.find(selectionUtils.getSelectedRange(editor).startPath().elements, function (el) {
        return el.getAttribute('class') === 'wixGuard';
    });
    if (guard) {
        var range = editor.createRange();
        range.setStartAfter(guard);
        range.select();
    }
}

function removeList(editor) {
    if (editor.getCommand('numberedlist').state !== CKEDITOR.TRISTATE_OFF) {
        editor.execCommand('numberedlist');
        setCursorOutOfGuardIfNeeded(editor);
    } else if (editor.getCommand('bulletedlist').state !== CKEDITOR.TRISTATE_OFF) {
        editor.execCommand('bulletedlist');
        setCursorOutOfGuardIfNeeded(editor);
    }
}

function shouldAddGuardToBlock(editor, block, range, keyCode) {
    if (range.collapsed) {
        var blockTextLength = utils.removeGuardsChars(block.$.textContent).length;
        if (blockTextLength > 1) {
            //we have multiple characters no risk with backspace\delete of empty text
            return false;
        }

        if (guardUtils.hasGuard(block)) {
            return false;
        }

        if (blockTextLength === 0) {
            //the block text is empty
            return true;
        }
        if (isBackspace(keyCode) && elementUtils.getTextBeforeCursor(range, block).length) {
            //backspace key will delete the last character
            return true;
        }

        if (isDeleteKey(keyCode) && !elementUtils.getTextBeforeCursor(range, block).length) {
            //delete key will delete the last character
            return true;
        }
    }

    //range is not collapsed
    return selectionUtils.isStartBlockTextFullySelected(editor, block);
}

function handleEditorFirstTextBlock(event) {
    var editor = event.sender.editor,
        keyCode = event.data.$.keyCode;
    if (shouldStopKeyDownEvent(editor, keyCode)) {
        if (isBackspace(keyCode)) {
            // Remove bullets or numbered list if backspace was pressed
            removeList(editor, keyCode);
        }

        stopEvent(event);
        return;
    }
    handleNormalTextBlock(event);
}

function handleExistingGuard(range, keyCode, editor, startBlock) {
    //if guard is selected and only guard is selected we want to move cursor to proper position
    //if many lines(brs) are selected with guard then selection is not to be considered as only guard selected
    //"proper position" - meaning the guard will not be deleted by mistake
    if (range.collapsed || (rangeUtils.getText(range) === CONSTANTS.WIX_GUARD && rangeUtils.countBrInRange(range) < 2)) {
        if (isBackspace(keyCode)) {
            guardUtils.setCursorBeforeGuard(editor, startBlock);
        } else {
            guardUtils.setCursorAfterGuard(editor, startBlock);
        }
    } else {
        guardUtils.moveSelectionAfterGuard(editor, startBlock);
    }
}

function addGuardToNormalBlock(editor, startBlock, keyCode) {
    var bookmark = selectionUtils.createBookmark(editor);
    if (startBlock.getText().length === 0) {
        //there is no text, we want to set guard @ cursor position. If is backspace we must set guard AFTER the cursor
        //otherwise backspace will delete our just added guard
        guardUtils.insertGuard(editor, startBlock, bookmark && bookmark.startNode, isBackspace(keyCode));
    } else {
        //When delete key is pressed, we have to insert the guard BEFORE the cursor and not after
        //Otherwise the delete key will remove the guard instead of the real character after the cursor
        guardUtils.insertGuard(editor, startBlock, isDeleteKey(keyCode) && bookmark && bookmark.startNode);
    }
    selectionUtils.moveToBookmark(editor, bookmark);
}

function handleNormalTextBlock(event) {
    var editor = event.sender.editor,
        keyCode = event.data.$.keyCode,
        startBlock = utils.getBlockParent(selectionUtils.getSelectedRange(editor).startPath()),
        range = selectionUtils.getSelectedRange(editor),
        bookmark;

    if (selectionUtils.countBrTagsBeforeCursor(editor, startBlock) > 1) {
        //text is not empty, we have br tags before cursor
        //that's not great. we would like to have a guard if all there is are brs, and worry about inserting it before the first br
        //we would really like to have a guardUtils.appendGuardTo(element)
        return;
    }

    if (guardUtils.hasGuard(startBlock)) {
        handleExistingGuard(range, keyCode, editor, startBlock);
        return;
    }

    if (shouldAddGuardToBlock(editor, startBlock, range, keyCode)) {
        if (range.collapsed) {
            addGuardToNormalBlock(editor, startBlock, keyCode);
        } else {
            bookmark = selectionUtils.createBookmark(editor, utils.isIE());

            guardUtils.insertGuard(editor, startBlock, bookmark && bookmark.startNode);
            selectionUtils.moveToBookmark(editor, bookmark);
        }
    }
}


function safeDeleteRangeContents(range, editor) {
    var startBlock = utils.getBlockParent(range.startPath());
    var endBlock = utils.getBlockParent(range.endPath());
    var multipleBlocksSelectedBeforeDelete = !startBlock.equals(endBlock);

    range.deleteContents(true);

    var isEndBlockEmpty = elementUtils.isElementTextEmpty(endBlock);
    if (multipleBlocksSelectedBeforeDelete && isEndBlockEmpty) {
        //we have just deleted multiple blocks and the last block is empty
        //Empty end block should have been delete
        //probably still here because of ck wrong decision
        range.collapse(true);

        var endBlockHighestAncestor = _.find(endBlock.getParents(), function (element) {
            return editor.editable().equals(element.getParent());
        });

        var isAncestorOfStartBlock = _.find(startBlock.getParents(), function (element) {
            return element.equals(endBlockHighestAncestor);
        });

        if (endBlockHighestAncestor && !isAncestorOfStartBlock) {
            endBlockHighestAncestor.remove();
        }
    }
}

function handleShiftEnter(event, editor) {
    var range = selectionUtils.getSelectedRange(editor);
    var startBlock = utils.getBlockParent(range.startPath());

    if (!range.collapsed) {
        editor.undoManager.unlock();
        safeDeleteRangeContents(range, editor);
        if (shouldAddGuardToBlock(editor, startBlock, range, CONSTANTS.ENTER_KEYCODE)) {
            var bookmark = selectionUtils.createBookmark(editor);
            if (bookmark) {
                guardUtils.insertGuard(editor, startBlock, bookmark.startNode);
            } else {
                guardUtils.insertGuard(editor, startBlock, range.startContainer);
            }

            selectionUtils.moveToBookmark(editor, bookmark);

            //todo - check if needed
            range = selectionUtils.getSelectedRange(editor);
            range.collapse(true);
        }
        range.select();
        editor.undoManager.lock();
    }
    if (!guardUtils.guardInSelection(editor, startBlock)) {
        return;
    }
    handleNormalTextBlock(event);
}

function hijackCKFillingChar(editor) {
    var fillingChar = editor.editable().getCustomData('cke-fillingChar');
    if (fillingChar) {
        editor.WIX.hijackedFillingChars.push(fillingChar);
        editor.editable().removeCustomData('cke-fillingChar');
    }
}

function hijackWixGuard(editor) {
    var range = selectionUtils.getSelectedRange(editor);
    var startPath = range.startPath(),
        rangeStartParentBlock = utils.getBlockParent(startPath),
        guardStartBlock = guardUtils.getGuard(rangeStartParentBlock);
    if (!guardStartBlock) {
        return;
    }
    guardStartBlock.removeClass('wixGuard');
    editor.WIX.hijackedWixGuards = editor.WIX.hijackedWixGuards || [];
    editor.WIX.hijackedWixGuards.push({
        guard: guardStartBlock,
        parentBlock: rangeStartParentBlock
    });
    if (guardStartBlock.getChild(0).type === CKEDITOR.NODE_TEXT) {
        editor.WIX.hijackedFillingChars.push(guardStartBlock.getChild(0));
    }
}

function handleCombiningKeys(editor) {
    hijackWixGuard(editor);
    if (!CKEDITOR.env.webkit) {
        return;
    }
    editor.WIX.hijackedFillingChars = editor.WIX.hijackedFillingChars || [];
    hijackCKFillingChar(editor);
}

function handleKeyDown(event) {
    var editor = event.sender.editor;

    if (!selectionUtils.hasSelectedRange(editor)) {
        selectionUtils.setSelectionAt(editor, CONSTANTS.CURSOR_LOCATIONS.END);
    }

    if (shouldIgnoreKeyDownEvent(event)) {
        return;
    }
    if (isCombiningKeys(event)) {
        handleCombiningKeys(editor);
        return;
    }
    editor.undoManager.lock();
    selectionUtils.fixSelectionOnBodyIfNeeded(editor);
    guardUtils.removeUneededGuards(editor);
    if (isShiftEnter(event)) {
        handleShiftEnter(event, editor);
    } else if (selectionStartsOnFirstBlock(editor)) {
        handleEditorFirstTextBlock(event);
    } else {
        handleNormalTextBlock(event);
    }

    handleRemovingListItem(event.data.$.keyCode, editor);

    editor.undoManager.unlock();
}

function handleRemovingListItem(keyCode, editor) {
    if (!isBackspace(keyCode)) {
        return;
    }

    var emptyListItem = getEnclosingEmptyListItem(editor);
    if (!emptyListItem) {
        return;
    }

    var previousItem = emptyListItem.getPrevious();
    if (!previousItem || previousItem.getName() !== 'li') {
        return;
    }

    var previousItemTextNode = selectionUtils.findLastTextNode(editor, previousItem);
    if (!previousItemTextNode) {
        return;
    }
    /**
     * CK will try to fix the position of the cursor, which will result
     * in a cursor positioned outside of the last <span> element (if exists) and typed text
     * may not have the same style as the existing text in the list item.
     * We're deferring to make sure we fix the selection after CKEditor's failed attempt.
     */
    _.defer(function () {
        var range = editor.createRange();
        range.setStartAfter(previousItemTextNode);
        range.setEndAfter(previousItemTextNode);
        range.collapse();
        range.select();
    });
}

function getEnclosingEmptyListItem(editor) {
    var range = selectionUtils.getSelectedRange(editor);
    if (!range || !range.collapsed) {
        return null;
    }

    var listItem = elementUtils.getElementFromPath(range.startPath(), 'li');
    if (listItem && elementUtils.isElementTextEmpty(listItem)) {
        return listItem;
    }
}

export default {
    handleKeyDown: handleKeyDown
};
